import { GET } from '@/api/base/base-request';
import { ENDPOINTS } from '@/api/endpoints';
import { errorlog } from '@/libs/error-logs';
import { toast_error } from '@/libs/toast-wrappers';
import { toError } from '@/utils/errors';

/**
 * Status of Vitazi Insights processing for a single image
 */
export type InsightsStatus = 'not_started' | 'processing' | 'completed' | 'failed';

/**
 * Insights processing status for a single image
 */
export interface ImageInsightsStatus {
  image_filename: string;
  status: InsightsStatus;
  processed_at: string | null;
  error_message: string | null;
}

/**
 * API response for insights status endpoint
 */
export interface InsightsStatusResponse {
  data: ImageInsightsStatus[];
}

/**
 * Fetches the Insights processing status for all images in a case
 * @param caseId - The case ID to fetch insights status for
 * @returns Promise with the insights status response
 * @throws Error if the request fails
 */
export async function getCaseInsightsStatus(caseId: string): Promise<InsightsStatusResponse> {
  try {
    const response = await GET<InsightsStatusResponse>(ENDPOINTS.CASE_IMAGES_INSIGHTS_STATUS, {
      parameters: { caseId },
      enableErrorOn404: false, // 404 is expected for new cases without insights data
    });
    return response;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/case-images/getCaseInsightsStatus',
      'Error fetching case insights status'
    );

    // Only show toast error for non-404 errors
    if (error instanceof Error && !error.message.includes('404')) {
      toast_error('Failed to fetch insights processing status');
    }

    return Promise.reject(toError(error));
  }
}
